import { IViolationList } from '../classes/Domain';
import { QualitasStateCode, isAllowedState } from './StateQuoteConfig';

/** D2C aggregated violation button → representative ViolationList Name. */
export type D2cViolationGroupDef = {
    /** Label shown on /quick/driver (docx / Excel D2C). */
    label: string;
    /** Exact `text` in ViolationList for this state; its `value` goes to violationCode. */
    representativeName: string;
};

/**
 * Phase 3.2 — only groups with Excel D2C filled for the state.
 * Codes resolved at runtime from ViolationList by representativeName (see D2C_VIOLATION_MAPPING.md).
 */
export const D2C_VIOLATION_GROUPS_BY_STATE: Record<QualitasStateCode, ReadonlyArray<D2cViolationGroupDef>> = {
    CA: [
        { label: 'At-fault accident', representativeName: 'Chargeable Accident - No Injury' },
        { label: 'Driving while Suspended/Revoked', representativeName: 'Driving while Suspended/Revoked' },
        { label: 'DUI/Open Container', representativeName: 'Drunk Driving - No Injury' },
        { label: 'Evaded Peace Officer', representativeName: 'Evade Peace Officer - No Injury' },
        { label: 'Faulty Equipment', representativeName: 'Other Equipment' },
        { label: 'Hit and run', representativeName: 'Hit and Run' },
        { label: 'Minor traffic violation (1 point)', representativeName: 'Other Minor' },
        { label: 'Other Felony/Major Conviction', representativeName: 'Other Felony Involving Vehicle' },
        { label: 'Reckless driving', representativeName: 'Reckless Driving - No Injury' },
        { label: 'Speeding', representativeName: 'Speeding Over 65' },
        { label: 'Speeding over 100', representativeName: 'Speeding Over 100' },
        { label: 'Unlicensed Driving', representativeName: 'Unlicensed Driving' },
    ],
    TX: [
        { label: 'At-fault accident', representativeName: 'Chargeable Accident - No Injury' },
        { label: 'Driving while Suspended/Revoked/Unlicensed', representativeName: 'Driving while Suspended/Revoked' },
        { label: 'DUI/Open Container', representativeName: 'Drinking While Driving' },
        { label: 'Other Felony/Major Conviction', representativeName: 'Malicious Mischief/Tampering' },
        { label: 'Reckless driving', representativeName: 'Reckless Driving - No Injury' },
    ],
};

export type D2cViolationSelectOption = {
    label: string;
    value: string;
};

function findCatalogValue(violationList: IViolationList, representativeName: string): string | null {
    const want = representativeName.trim();
    for (const group of violationList.violationGroups || []) {
        for (const item of group.violations || []) {
            if ((item.text || '').trim() === want) {
                return item.value;
            }
        }
    }
    return null;
}

/**
 * Flat select options for Qualitas D2C: aggregated labels, values from ViolationList.
 * Returns null when state has no D2C map (caller may fall back to full catalog).
 * Groups whose representative Name is missing from the catalog are omitted.
 */
export function buildD2cViolationSelectOptions(
    stateCode: string | null | undefined,
    violationList: IViolationList | null | undefined
): D2cViolationSelectOption[] | null {
    if (!isAllowedState(stateCode) || !violationList?.violationGroups) {
        return null;
    }

    const defs = D2C_VIOLATION_GROUPS_BY_STATE[stateCode];
    const options: D2cViolationSelectOption[] = [];

    for (const def of defs) {
        const value = findCatalogValue(violationList, def.representativeName);
        if (!value) {
            continue;
        }
        options.push({ label: def.label, value });
    }

    return options;
}
